import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWizard } from '../../context/WizardContext';
import WizardProgress from '../../components/WizardProgress';
import { getDataSourceData, listDataSources, DataSource } from '../../api/client';
import '../MappingWizard.css';

const Step4Relationships: React.FC = () => {
  const navigate = useNavigate();
  const { state, saveDraft } = useWizard();
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  const [relationships, setRelationships] = useState(state.relationships);
  const [masterFields, setMasterFields] = useState<string[]>([]);
  const [detailFields, setDetailFields] = useState<Record<string, string[]>>({});
  const [dataSourceNames, setDataSourceNames] = useState<Record<string, string>>({});

  const detailSources = state.dataSources.filter((s) => s.type === 'detail');
  const primarySource = state.dataSources.find((s) => s.role === 'primary');

  useEffect(() => {
    // Load data source names
    const loadDataSourceNames = async () => {
      try {
        const allSources = await listDataSources();
        const namesMap: Record<string, string> = {};
        allSources.forEach((ds: DataSource) => {
          namesMap[ds.id] = ds.name;
        });
        setDataSourceNames(namesMap);
      } catch (err) {
        // Error silently handled
      }
    };
    loadDataSourceNames();

    if (primarySource) {
      loadFields(primarySource.id, 'master');
    }
    detailSources.forEach((ds) => loadFields(ds.id, 'detail'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFields = async (sourceId: string, type: 'master' | 'detail') => {
    try {
      const response = await getDataSourceData(sourceId);
      // Use column_order if available, otherwise fall back to Object.keys
      const fields = response.column_order && response.column_order.length > 0
        ? response.column_order
        : Object.keys(response.data || {});

      if (type === 'master') {
        setMasterFields(fields);
      } else {
        setDetailFields((prev) => ({ ...prev, [sourceId]: fields }));
      }
    } catch (err) {
      // Error silently handled
    }
  };

  const addRelationship = () => {
    if (!primarySource || detailSources.length === 0) return;

    setRelationships([
      ...relationships,
      {
        master_source_id: primarySource.id,
        master_field: '',
        detail_source_id: detailSources[0].id,
        detail_field: '',
      },
    ]);
  };

  const updateRelationship = (index: number, field: string, value: string) => {
    const updated = [...relationships];
    updated[index] = { ...updated[index], [field]: value };
    setRelationships(updated);
  };

  const removeRelationship = (index: number) => {
    setRelationships(relationships.filter((_, i) => i !== index));
  };

  const handleNext = async () => {
    // Validate relationships if detail sources exist
    if (detailSources.length > 0 && relationships.length === 0) {
      alert('Bitte definiere mindestens eine Beziehung');
      return;
    }

    for (const rel of relationships) {
      if (!rel.master_field || !rel.detail_field) {
        alert('Bitte fülle alle Beziehungsfelder aus');
        return;
      }
    }

    setSaving(true);
    try {
      await saveDraft(4, { relationships });
      navigate('/wizard/mapping');
    } catch (err: any) {
      alert(`Fehler beim Speichern: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      await saveDraft(4, { relationships });
      alert('Entwurf gespeichert!');
    } catch (err: any) {
      alert(`Fehler beim Speichern: ${err.response?.data?.error || err.message}`);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleBack = () => {
    navigate('/wizard/datasources');
  };

  const canSkip = detailSources.length === 0;

  return (
    <>
      <WizardProgress currentStep={4} totalSteps={5} />
      <div className="wizard-step">
        <h2>Beziehungen definieren</h2>
        <p className="step-description">
          Definiere, wie Stamm- und Detaildaten verknüpft sind (z.B. Person.id → Attendance.person_id).
        </p>

        {canSkip ? (
          <div className="info-box">
            <p>Du hast keine Detaildaten-Vorlagen hinzugefügt. Dieser Schritt kann übersprungen werden.</p>
          </div>
        ) : (
          <>
            {relationships.length === 0 ? (
              <div className="empty-state">
                <p>Keine Beziehungen definiert</p>
                <button onClick={addRelationship} className="btn-primary">
                  + Beziehung hinzufügen
                </button>
              </div>
            ) : (
              <div className="relationships-list">
                {primarySource && (
                  <div className="info-box" style={{ marginBottom: '1rem', padding: '0.75rem 1rem' }}>
                    <strong>Stammdaten-Vorlage:</strong> {dataSourceNames[primarySource.id] || primarySource.id}
                  </div>
                )}
                {relationships.map((rel, index) => (
                  <div key={index} className="relationship-item" style={{
                    padding: '1.5rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    background: '#fafafa'
                  }}>
                    <div className="relationship-row" style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto 1fr 1fr auto',
                      gap: '1rem',
                      alignItems: 'end'
                    }}>
                      <div className="field-group">
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#555' }}>
                          Stammdaten-Feld ({dataSourceNames[primarySource?.id || ''] || 'Stammdaten'})
                        </label>
                        <select
                          value={rel.master_field}
                          onChange={(e) => updateRelationship(index, 'master_field', e.target.value)}
                          style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                        >
                          <option value="">-- Wählen --</option>
                          {masterFields.map((f) => (
                            <option key={f} value={f}>{f}</option>
                          ))}
                        </select>
                      </div>
                      <span className="arrow" style={{ fontSize: '1.5rem', color: '#006355', paddingBottom: '0.5rem' }}>→</span>
                      <div className="field-group">
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#555' }}>Detaildaten-Vorlage</label>
                        <select
                          value={rel.detail_source_id}
                          onChange={(e) => updateRelationship(index, 'detail_source_id', e.target.value)}
                          style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                        >
                          {detailSources.map((ds) => (
                            <option key={ds.id} value={ds.id}>{dataSourceNames[ds.id] || ds.id}</option>
                          ))}
                        </select>
                      </div>
                      <div className="field-group">
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#555' }}>Detaildaten-Feld</label>
                        <select
                          value={rel.detail_field}
                          onChange={(e) => updateRelationship(index, 'detail_field', e.target.value)}
                          style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                        >
                          <option value="">-- Wählen --</option>
                          {(detailFields[rel.detail_source_id] || []).map((f) => (
                            <option key={f} value={f}>{f}</option>
                          ))}
                        </select>
                      </div>
                      <button onClick={() => removeRelationship(index)} className="btn-remove" style={{ marginBottom: '0.25rem' }}>
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
                <button onClick={addRelationship} className="btn-secondary">
                  + Weitere Beziehung
                </button>
              </div>
            )}
          </>
        )}

        <div className="wizard-actions">
          <button onClick={handleBack} className="btn-secondary">
            ← Zurück
          </button>
          <button
            onClick={handleSaveDraft}
            className="btn-secondary"
            disabled={savingDraft || saving}
            style={{ marginLeft: 'auto' }}
          >
            {savingDraft ? 'Speichere...' : 'Entwurf speichern'}
          </button>
          <button onClick={handleNext} className="btn-primary" disabled={saving || savingDraft}>
            {saving ? 'Speichere...' : 'Weiter →'}
          </button>
        </div>
      </div>
    </>
  );
};

export default Step4Relationships;
